// Payroll - Processing Tab (Payroll Officer generates monthly & supplementary payrolls)
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';

const ProcessingTab = ({ api, user, armyTypeId, armyTypeName, groups }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });

  const canGenerate = hasAnyRole(user, ['admin', 'payroll_officer']);

  useEffect(() => {
    fetchPayrolls();
    if (groups && groups.length > 0) setGroupId(String(groups[0].id));
  }, [armyTypeId, groups]);

  const fetchPayrolls = async () => {
    try {
      const res = await api.get(`/payrolls?army_type_id=${armyTypeId}`);
      const all = res?.payrolls || res?.data?.payrolls || (Array.isArray(res) ? res : []);
      const filtered = all.filter(p => {
        const forceId = p.payroll_group?.army_type_id || p.army_type_id || (groups || []).find(g => String(g.id) === String(p.payroll_group_id))?.army_type_id;
        return !armyTypeId || String(forceId) === String(armyTypeId);
      });
      setPayrolls(filtered);
    } catch (e) { console.error('Error fetching payrolls:', e); }
  };

  const handleGenerate = async () => {
    if (!groupId) return setMsg({ text: 'Please select a payroll group', type: 'error' });
    if (!confirm(`Generate payroll for ${armyTypeName} — Period: ${period}?`)) return;
    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const res = await api.post('/payrolls', { payroll_group_id: groupId, payroll_period: period });
      if (res?.response && !res.response.ok) {
        const err = res.data?.error || res.data?.errors?.join(', ') || 'Failed to generate payroll';
        setMsg({ text: `❌ ${err}`, type: 'error' });
      } else {
        setMsg({ text: `✅ Payroll for ${period} generated successfully. Status: Draft.`, type: 'success' });
        fetchPayrolls();
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Error generating payroll'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const handleRegenerate = async (id, periodStr) => {
    if (!confirm(`Regenerate Draft Payroll for period ${periodStr}? This will refresh all calculations.`)) return;
    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const res = await api.post(`/payrolls/${id}/process_regeneration`);
      if (res?.response && !res.response.ok) {
        setMsg({ text: `❌ ${res.data?.error || 'Failed to regenerate payroll'}`, type: 'error' });
      } else {
        setMsg({ text: `✅ Payroll for ${periodStr} regenerated successfully.`, type: 'success' });
        fetchPayrolls();
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Error regenerating payroll'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const handleSupplementary = async (id, periodStr) => {
    const reason = prompt(`Enter reason for creating Supplementary (Adjustment) Payroll for ${periodStr}:`);
    if (!reason) return;
    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const res = await api.post(`/payrolls/${id}/supplementary`, { reason });
      if (res?.response && !res.response.ok) {
        setMsg({ text: `❌ ${res.data?.error || 'Failed to create supplementary payroll'}`, type: 'error' });
      } else {
        setMsg({ text: `✅ Supplementary Payroll created for period ${periodStr}.`, type: 'success' });
        fetchPayrolls();
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Error creating supplementary payroll'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const handleDeleteDraft = async (id, periodStr) => {
    if (!confirm(`Delete draft payroll run for ${periodStr}? This action cannot be undone.`)) return;
    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const res = await api.delete(`/payrolls/${id}`);
      if (res?.response && !res.response.ok) {
        setMsg({ text: `❌ ${res.data?.error || 'Failed to delete payroll'}`, type: 'error' });
      } else {
        setMsg({ text: `✅ Draft payroll for period ${periodStr} deleted successfully.`, type: 'success' });
        fetchPayrolls();
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Error deleting payroll'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const statusColors = {
    draft:     { bg: '#f1f5f9', text: '#475569' },
    reviewed:  { bg: '#ede9fe', text: '#5b21b6' },
    approved:  { bg: '#dcfce7', text: '#166534' },
    finalized: { bg: '#fef3c7', text: '#92400e' },
    paid:      { bg: '#cffafe', text: '#155e75' },
  };

  return (
    <div>
      <div style={{ marginBottom: 20, padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
        <strong>📋 Payroll Processing Flow:</strong> Payroll Officer generates monthly payroll (Draft) →
        Finance Officer reviews (Reviewed) → MoF Director General approves (Approved) → Accountant General final approves & locks (Finalized) → Generate Payment Files → Mark Paid.
      </div>

      {canGenerate && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>Generate Monthly Payroll</h3>
          {msg.text && (
            <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              background: msg.type === 'error' ? '#fee2e2' : '#dcfce7',
              color: msg.type === 'error' ? '#991b1b' : '#166534',
              border: `1px solid ${msg.type === 'error' ? '#fca5a5' : '#86efac'}` }}>
              {msg.text}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Payroll Group</label>
              <select value={groupId} onChange={e => setGroupId(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                <option value="">Select Group</option>
                {(groups || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Payroll Period (YYYY-MM)</label>
              <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>
            <button onClick={handleGenerate} disabled={loading}
              style={{ padding: '10px 24px', background: loading ? '#94a3b8' : '#1b4332', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '⏳ Generating...' : '▶ Generate Payroll'}
            </button>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#6b7280' }}>
            ℹ Draft payrolls can be regenerated before final approval. Once finalized or paid, use <strong>Supplementary Payroll</strong> for backdated adjustments.
          </p>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Payroll Runs — {armyTypeName}</h3>
          <button onClick={fetchPayrolls} style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
        </div>
        <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: 850, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Period', 'Group', 'Status', 'Headcount', 'Total Net Pay', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payrolls.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                  No payroll runs found for this force. {canGenerate ? 'Use the form above to generate one.' : 'Contact a Payroll Officer to generate payroll.'}
                </td></tr>
              )}
              {payrolls.map((p, i) => {
                const sc = statusColors[p.status] || statusColors.draft;
                return (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, whiteSpace: 'nowrap' }}>{p.payroll_period}</td>
                    <td style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{p.payroll_group?.name || '—'}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.text }}>
                        {p.status?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>{p.items_count || '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>
                      {p.total_net ? `$${parseFloat(p.total_net).toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {canGenerate && ['draft', 'reviewed'].includes(p.status) && (
                        <button onClick={() => handleRegenerate(p.id, p.payroll_period)}
                          style={{ padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 6 }}>
                          🔄 Regenerate
                        </button>
                      )}
                      {canGenerate && p.status === 'draft' && (
                        <button onClick={() => handleDeleteDraft(p.id, p.payroll_period)}
                          style={{ padding: '4px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 6 }}>
                          🗑️ Delete
                        </button>
                      )}
                      {canGenerate && ['finalized', 'paid'].includes(p.status) && (
                        <button onClick={() => handleSupplementary(p.id, p.payroll_period)}
                          style={{ padding: '4px 10px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          ➕ Supplementary
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProcessingTab;
